import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { throttle } from "lodash";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import { Accordion, AccordionTab } from "primereact/accordion";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { Chip } from "primereact/chip";
import authApi from "../../../service/IdApi";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";
import { useAppContext } from "../../../context/appContext";

const HAS_VARIANTS_OPTIONS = [
    { label: "Has variants", value: "yes" },
    { label: "No variants", value: "no" },
];

const OBJECT_ID_PATTERN = /^[a-fA-F0-9]{24}$/;

const isValidObjectId = (value) => OBJECT_ID_PATTERN.test(String(value || "").trim());

const SORT_OPTIONS = [
    { label: "Date (Newest First)", value: "uploadedAt_desc", sortField: "uploadedAt", sortOrder: -1 },
    { label: "Date (Oldest First)", value: "uploadedAt_asc", sortField: "uploadedAt", sortOrder: 1 },
    { label: "Name (A-Z)", value: "name_asc", sortField: "friendlyImageName", sortOrder: 1 },
    { label: "Name (Z-A)", value: "name_desc", sortField: "friendlyImageName", sortOrder: -1 },
];

const VIEWPORT_OPTIONS = [
    { label: "Mobile", value: "mobile", icon: "pi pi-mobile", width: 600 },
    { label: "Tablet", value: "tablet", icon: "pi pi-tablet", width: 1280 },
    { label: "Desktop", value: "desktop", icon: "pi pi-desktop", width: 1920 },
];

const DetailRow = ({ label, children }) => {
    if (children == null || children === "") {
        return null;
    }
    return (
        <div className="mb-2">
            <div className="text-xs text-500 mb-1">{label}</div>
            <div className="text-sm word-break-all">{children}</div>
        </div>
    );
};

const hasUsedFor = (variant, role) => {
    if (!variant?.usedFor || !role) {
        return false;
    }
    for (let i = 0; i < variant.usedFor.length; i++) {
        if (variant.usedFor[i] === role) {
            return true;
        }
    }
    return false;
};

const getWidthVariants = (item) => {
    if (!item?.variants) {
        return [];
    }
    const widthVariants = [];
    for (let i = 0; i < item.variants.length; i++) {
        const variant = item.variants[i];
        if (!variant || !hasUsedFor(variant, "display") || !variant.width || !variant.publicUrl) {
            continue;
        }
        widthVariants.push(variant);
    }
    widthVariants.sort((a, b) => a.width - b.width);
    return widthVariants;
};

const getSortedVariants = (item) => {
    if (!item?.variants) {
        return [];
    }
    return [...item.variants].sort((a, b) => {
        const aWidth = a?.width != null ? a.width : 0;
        const bWidth = b?.width != null ? b.width : 0;
        return aWidth - bWidth;
    });
};

const buildSrcSet = (item) => {
    const widthVariants = getWidthVariants(item);
    if (widthVariants.length === 0) {
        return undefined;
    }
    return widthVariants.map((variant) => `${variant.publicUrl} ${variant.width}w`).join(", ");
};

const getImageSrc = (item) => {
    const widthVariants = getWidthVariants(item);
    if (widthVariants.length > 0) {
        return widthVariants[widthVariants.length - 1].publicUrl;
    }
    return item?.publicPath || undefined;
};

const isVideoItem = (item) => {
    const name = (item?.imageName || item?.publicPath || "").toLowerCase();
    return name.endsWith(".mp4") || name.endsWith(".webm") || name.endsWith(".mov");
};

const isGifItem = (item) => {
    const name = (item?.imageName || item?.publicPath || "").toLowerCase();
    return name.endsWith(".gif");
};

const isUnsupportedForVariants = (item) => {
    return isVideoItem(item) || isGifItem(item);
};

/** True only when size variants have been generated (strict boolean). */
const hasSizeVariants = (item) => item?.hasVariants === true;

const formatCatalogChipLabel = (item) => {
    const name = item?.name || item?.uri || "";
    if (item?.confidence == null || Number.isNaN(Number(item.confidence))) {
        return name;
    }
    const percent = Math.round(Number(item.confidence) * 100);
    return name + " · " + percent + "%";
};

const RemovableCatalogTags = ({ label, kind, items, removingKey, onRemove, allowEmpty = false }) => {
    const list = Array.isArray(items) ? items.slice() : [];
    list.sort((a, b) => {
        const aConfidence =
            a?.confidence == null || Number.isNaN(Number(a.confidence)) ? -1 : Number(a.confidence);
        const bConfidence =
            b?.confidence == null || Number.isNaN(Number(b.confidence)) ? -1 : Number(b.confidence);
        return bConfidence - aConfidence;
    });
    if (list.length === 0 && !allowEmpty) {
        return null;
    }
    return (
        <div className="mb-3">
            <style>{`
                .gallery-vision-chip.p-chip {
                    font-size: 14px;
                    padding: 0.1rem 0.35rem;
                    gap: 0.25rem;
                }
                .gallery-vision-chip.p-chip .p-chip-text {
                    font-size: 14px;
                    line-height: 1.25;
                    margin: 0;
                }
                .gallery-vision-chip.p-chip .p-chip-remove-icon {
                    font-size: 0.75rem;
                    margin-left: 0.15rem;
                }
            `}</style>
            <div className="text-xs font-semibold text-500 uppercase mb-1 flex align-items-center gap-2">
                <span>{label}</span>
                <span className="font-normal normal-case text-400">
                    ({list.length}){list.length > 0 ? " - click x to remove" : " - none matched"}
                </span>
            </div>
            {list.length > 0 && (
                <div className="flex flex-column align-items-start gap-1 overflow-y-auto" style={{ maxHeight: "11rem" }}>
                    {list.map((item) => {
                        if (!item?.uri) {
                            return null;
                        }
                        const key = kind + ":" + item.uri;
                        const isRemoving = removingKey === key;
                        return (
                            <Chip
                                key={key}
                                label={formatCatalogChipLabel(item)}
                                removable={!isRemoving}
                                onRemove={() => {
                                    onRemove(kind, item.uri);
                                }}
                                className={isRemoving ? "opacity-50 gallery-vision-chip" : "gallery-vision-chip"}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const formatFileSize = (bytes) => {
    if (bytes == null || Number.isNaN(Number(bytes))) {
        return null;
    }
    const value = Number(bytes);
    if (value < 1024) {
        return value + " B";
    }
    if (value < 1024 * 1024) {
        return (value / 1024).toFixed(1) + " KB";
    }
    return (value / (1024 * 1024)).toFixed(1) + " MB";
};

const formatUsedFor = (variant) => {
    if (!variant?.usedFor || variant.usedFor.length === 0) {
        return null;
    }
    return variant.usedFor.join(", ");
};

const variantLabel = (variant) => {
    if (hasUsedFor(variant, "display") && variant.width != null) {
        return "Display · " + variant.width + "w";
    }
    if (hasUsedFor(variant, "original")) {
        return "Original";
    }
    if (hasUsedFor(variant, "cs")) {
        return "Conversion source";
    }
    return formatUsedFor(variant) || "Variant";
};

const formatDate = (value) => {
    if (!value) {
        return null;
    }
    try {
        return new Date(value).toLocaleString();
    } catch (e) {
        return String(value);
    }
};

const resolveSortOption = (sortValue) => {
    for (let i = 0; i < SORT_OPTIONS.length; i++) {
        if (SORT_OPTIONS[i].value === sortValue) {
            return SORT_OPTIONS[i];
        }
    }
    return SORT_OPTIONS[0];
};

const viewportTemplate = (option) => {
    if (!option) {
        return null;
    }
    return (
        <span className="flex align-items-center gap-2">
            <i className={option.icon} />
            <span>{option.label}</span>
        </span>
    );
};

const CustomerGallery = () => {
    const { displayAlert } = useAppContext();
    const [items, setItems] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyState, setLazyState] = useState({
        first: 0,
        rows: 25,
        page: 0,
        sortValue: SORT_OPTIONS[0].value,
        hasVariants: "",
        providerId: "",
        searchText: "",
    });
    const [searchInput, setSearchInput] = useState("");
    const [providerIdInput, setProviderIdInput] = useState("");
    const searchTimerRef = useRef(null);
    const providerTimerRef = useRef(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showLightbox, setShowLightbox] = useState(false);
    const [previewViewport, setPreviewViewport] = useState("desktop");
    const [createVariantsLoading, setCreateVariantsLoading] = useState(false);
    const [pollingId, setPollingId] = useState(null);
    const [pollingTaskId, setPollingTaskId] = useState(null);
    const [variantErrorBaseline, setVariantErrorBaseline] = useState(null);
    const [reanalyzeLoading, setReanalyzeLoading] = useState(false);
    const [reanalyzePollingId, setReanalyzePollingId] = useState(null);
    const [reanalyzeTaskId, setReanalyzeTaskId] = useState(null);
    const [reanalyzeBaselineAt, setReanalyzeBaselineAt] = useState(null);
    const [removingVisionTagKey, setRemovingVisionTagKey] = useState(null);
    const [titleDraft, setTitleDraft] = useState("");
    const [titleSaving, setTitleSaving] = useState(false);
    const [bulkProvisionLoading, setBulkProvisionLoading] = useState(false);
    const [providerDialogVisible, setProviderDialogVisible] = useState(false);
    const [providerDialogLoading, setProviderDialogLoading] = useState(false);
    const [providerDialogData, setProviderDialogData] = useState(null);

    const selectedSort = resolveSortOption(lazyState.sortValue);

    const { isLoading, isError, refetch } = useQuery(["CustomerGallery", lazyState], async () => {
        const body = {
            first: lazyState.first,
            rows: lazyState.rows,
            sortField: selectedSort.sortField,
            sortOrder: selectedSort.sortOrder,
        };
        if (lazyState.hasVariants) {
            body.hasVariants = lazyState.hasVariants;
        }
        if (lazyState.providerId) {
            const providerId = lazyState.providerId.trim();
            if (isValidObjectId(providerId)) {
                body.providerId = providerId;
            }
        }
        if (lazyState.searchText) {
            body.searchText = lazyState.searchText.trim();
        }
        const result = await authApi.post("admin/customer-gallery/list", body);
        setItems(result.data?.items || []);
        setTotalRecords(result.data?.totalRecords || 0);
        return result.data;
    });

    useEffect(() => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
        searchTimerRef.current = setTimeout(() => {
            setLazyState((prev) => ({
                ...prev,
                first: 0,
                page: 0,
                searchText: searchInput,
            }));
        }, 400);
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, [searchInput]);

    useEffect(() => {
        if (providerTimerRef.current) {
            clearTimeout(providerTimerRef.current);
        }
        providerTimerRef.current = setTimeout(() => {
            const trimmed = providerIdInput.trim();
            if (trimmed && !isValidObjectId(trimmed)) {
                return;
            }
            setLazyState((prev) => ({
                ...prev,
                first: 0,
                page: 0,
                providerId: trimmed,
            }));
        }, 400);
        return () => {
            if (providerTimerRef.current) {
                clearTimeout(providerTimerRef.current);
            }
        };
    }, [providerIdInput]);

    const providerIdFilterInvalid = providerIdInput.trim() !== "" && !isValidObjectId(providerIdInput.trim());

    useEffect(() => {
        const nextTitle = selectedItem?.title || selectedItem?.vision?.title || "";
        setTitleDraft(nextTitle ? String(nextTitle) : "");
    }, [selectedItem?._id, selectedItem?.title, selectedItem?.vision?.title]);

    const mergeGalleryItem = (item) => {
        if (!item?._id) {
            return;
        }
        setItems((prev) => {
            const next = [];
            for (let i = 0; i < prev.length; i++) {
                if (prev[i]._id === item._id) {
                    next.push(item);
                } else {
                    next.push(prev[i]);
                }
            }
            return next;
        });
        setSelectedItem((prev) => (prev && prev._id === item._id ? item : prev));
    };

    const refreshGalleryItem = async (imageId) => {
        if (!imageId) {
            return null;
        }
        const result = await authApi.get("admin/customer-gallery/" + imageId);
        const item = result.data;
        if (item) {
            mergeGalleryItem(item);
        }
        return item;
    };

    const stopVariantPoll = () => {
        setPollingId(null);
        setPollingTaskId(null);
        setVariantErrorBaseline(null);
        setCreateVariantsLoading(false);
    };

    const stopReanalyzePoll = () => {
        setReanalyzePollingId(null);
        setReanalyzeTaskId(null);
        setReanalyzeBaselineAt(null);
        setReanalyzeLoading(false);
    };

    const latestVisionAnalyzerState = (item) => {
        const analyzers = item?.vision?.analyzers || [];
        let latestAt = null;
        let latestError = null;
        for (let i = 0; i < analyzers.length; i++) {
            if (analyzers[i]?.analyzedAt) {
                latestAt = analyzers[i].analyzedAt;
            }
            if (analyzers[i]?.analysisError) {
                latestError = analyzers[i].analysisError;
            }
        }
        return { latestAt, latestError };
    };

    useEffect(() => {
        if (!pollingId || !pollingTaskId) {
            return undefined;
        }
        let cancelled = false;
        const startedAt = Date.now();
        const maxPollMs = 15 * 60 * 1000;

        const finishVariantSuccess = (item) => {
            stopVariantPoll();
            displayAlert("success", "Variants ready", "Size variants were created successfully");
            if (item) {
                mergeGalleryItem(item);
            }
        };

        const finishVariantFailure = (message, item) => {
            stopVariantPoll();
            displayAlert("error", "Variant failed", message || "Size variant generation failed");
            if (item) {
                mergeGalleryItem(item);
            }
        };

        const poll = async () => {
            try {
                if (Date.now() - startedAt > maxPollMs) {
                    if (!cancelled) {
                        finishVariantFailure("Timed out waiting for size variant generation");
                    }
                    return;
                }
                const taskResult = await authApi.get("admin/media-tasks/" + pollingTaskId);
                const task = taskResult.data;
                if (cancelled || !task) {
                    return;
                }
                if (task.status === "FAILED") {
                    const item = await refreshGalleryItem(pollingId);
                    if (!cancelled) {
                        finishVariantFailure(
                            task.errorMessage || item?.variantProcessingError || "Size variant generation failed",
                            item
                        );
                    }
                    return;
                }
                if (task.status === "COMPLETED") {
                    const item = await refreshGalleryItem(pollingId);
                    if (cancelled) {
                        return;
                    }
                    if (item?.hasVariants) {
                        finishVariantSuccess(item);
                        return;
                    }
                    const errorMessage = item?.variantProcessingError;
                    const isNewError =
                        errorMessage &&
                        (!variantErrorBaseline || String(errorMessage) !== String(variantErrorBaseline));
                    if (isNewError) {
                        finishVariantFailure(errorMessage, item);
                        return;
                    }
                    finishVariantSuccess(item);
                    return;
                }
                await refreshGalleryItem(pollingId);
            } catch (e) {
                if (!cancelled) {
                    stopVariantPoll();
                    displayAlert(
                        "error",
                        "Variant poll failed",
                        e?.response?.data?.message || e?.message || "Could not check variant task status"
                    );
                }
            }
        };
        poll();
        const intervalId = setInterval(poll, 3000);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [pollingId, pollingTaskId, variantErrorBaseline, displayAlert]);

    useEffect(() => {
        if (!reanalyzePollingId || !reanalyzeTaskId) {
            return undefined;
        }
        let cancelled = false;
        const startedAt = Date.now();
        const maxPollMs = 15 * 60 * 1000;

        const poll = async () => {
            try {
                if (Date.now() - startedAt > maxPollMs) {
                    if (!cancelled) {
                        stopReanalyzePoll();
                        displayAlert("error", "Reanalyze timed out", "Timed out waiting for vision reanalysis");
                    }
                    return;
                }
                const taskResult = await authApi.get("admin/media-tasks/" + reanalyzeTaskId);
                const task = taskResult.data;
                if (cancelled || !task) {
                    return;
                }
                if (task.status === "FAILED") {
                    await refreshGalleryItem(reanalyzePollingId);
                    if (!cancelled) {
                        stopReanalyzePoll();
                        displayAlert(
                            "error",
                            "Reanalyze failed",
                            task.errorMessage || "Vision reanalysis failed"
                        );
                    }
                    return;
                }
                if (task.status === "COMPLETED") {
                    const item = await refreshGalleryItem(reanalyzePollingId);
                    if (cancelled) {
                        return;
                    }
                    const { latestAt, latestError } = latestVisionAnalyzerState(item);
                    const finished =
                        latestAt &&
                        (!reanalyzeBaselineAt || String(latestAt) !== String(reanalyzeBaselineAt));
                    stopReanalyzePoll();
                    if (latestError && finished) {
                        displayAlert("warn", "Vision reanalyze finished with error", latestError);
                    } else if (finished) {
                        displayAlert("success", "Vision ready", "Vision insights were updated");
                    } else {
                        displayAlert(
                            "success",
                            "Vision task completed",
                            "Vision reanalysis finished"
                        );
                    }
                    return;
                }
                await refreshGalleryItem(reanalyzePollingId);
            } catch (e) {
                if (!cancelled) {
                    stopReanalyzePoll();
                    displayAlert(
                        "error",
                        "Reanalyze poll failed",
                        e?.response?.data?.message || e?.message || "Could not check vision task status"
                    );
                }
            }
        };
        poll();
        const intervalId = setInterval(poll, 3000);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [reanalyzePollingId, reanalyzeTaskId, reanalyzeBaselineAt, displayAlert]);

    const openLightbox = (item) => {
        setSelectedItem(item);
        setShowLightbox(true);
        setPreviewViewport("desktop");
    };

    const closeLightbox = () => {
        setShowLightbox(false);
        setSelectedItem(null);
    };

    const onPageChange = (event) => {
        setLazyState((prev) => ({
            ...prev,
            first: event.first,
            rows: event.rows,
            page: event.page,
        }));
    };

    const createVariants = async () => {
        if (!selectedItem?._id || createVariantsLoading) {
            return;
        }
        setCreateVariantsLoading(true);
        const errorBaseline = selectedItem.variantProcessingError || null;
        setVariantErrorBaseline(errorBaseline);
        setSelectedItem((prev) => (prev ? { ...prev, variantProcessingError: null } : prev));
        setItems((prev) => {
            if (!Array.isArray(prev)) {
                return prev;
            }
            return prev.map((item) =>
                item?._id === selectedItem._id ? { ...item, variantProcessingError: null } : item
            );
        });
        try {
            const result = await authApi.post(
                "admin/customer-gallery/" + selectedItem._id + "/create-variants"
            );
            const taskId = result.data?.taskId;
            if (!taskId) {
                setCreateVariantsLoading(false);
                setVariantErrorBaseline(null);
                displayAlert("error", "Create variants failed", "No media task id was returned");
                return;
            }
            displayAlert("success", "Queued", "Size variant generation was queued");
            setPollingTaskId(taskId);
            setPollingId(selectedItem._id);
        } catch (e) {
            setCreateVariantsLoading(false);
            setVariantErrorBaseline(null);
            const message = e?.response?.data?.message || e?.message || "Failed to queue variant generation";
            displayAlert("error", "Create variants failed", message);
        }
    };

    const reanalyzeVision = async () => {
        if (!selectedItem?._id || reanalyzeLoading) {
            return;
        }
        setReanalyzeLoading(true);
        const { latestAt } = latestVisionAnalyzerState(selectedItem);
        setReanalyzeBaselineAt(latestAt);
        try {
            const result = await authApi.post(
                "admin/customer-gallery/" + selectedItem._id + "/reanalyze"
            );
            const taskId = result.data?.taskId;
            if (!taskId) {
                setReanalyzeLoading(false);
                setReanalyzeBaselineAt(null);
                displayAlert("error", "Reanalyze failed", "No media task id was returned");
                return;
            }
            displayAlert("success", "Queued", "Vision reanalysis was queued");
            setReanalyzeTaskId(taskId);
            setReanalyzePollingId(selectedItem._id);
        } catch (e) {
            setReanalyzeLoading(false);
            setReanalyzeBaselineAt(null);
            const message = e?.response?.data?.message || e?.message || "Failed to queue vision reanalysis";
            displayAlert("error", "Reanalyze failed", message);
        }
    };

    const removeVisionTag = async (kind, uri) => {
        if (!selectedItem?._id || !kind || !uri || removingVisionTagKey) {
            return;
        }
        const previousItem = selectedItem;
        const previousList = items;
        const key = kind + ":" + uri;
        const currentVision = selectedItem.vision || {};
        const nextList = Array.isArray(currentVision[kind])
            ? currentVision[kind].filter((item) => item?.uri !== uri)
            : [];
        const nextVision = {
            ...currentVision,
            [kind]: nextList,
        };
        const optimisticItem = {
            ...selectedItem,
            vision: nextVision,
            title: nextVision.title || selectedItem.title,
            description: nextVision.description || selectedItem.description,
        };
        setSelectedItem(optimisticItem);
        setItems((prev) => {
            if (!Array.isArray(prev)) {
                return prev;
            }
            return prev.map((item) => (item?._id === optimisticItem._id ? optimisticItem : item));
        });
        setRemovingVisionTagKey(key);
        try {
            const result = await authApi.post(
                "admin/customer-gallery/" + selectedItem._id + "/vision/remove-tag",
                { kind, uri }
            );
            const updated = result.data;
            if (updated) {
                setSelectedItem(updated);
                setItems((prev) => {
                    if (!Array.isArray(prev)) {
                        return prev;
                    }
                    return prev.map((item) => (item?._id === updated._id ? updated : item));
                });
            }
        } catch (error) {
            setSelectedItem(previousItem);
            setItems(previousList);
            const message =
                error?.response?.data?.message || error?.message || "Failed to remove vision tag";
            displayAlert("error", "Remove Tag Failed", message);
        } finally {
            setRemovingVisionTagKey(null);
        }
    };

    const savedVisionTitle = selectedItem?.title || selectedItem?.vision?.title || "";
    const titleDirty = String(titleDraft || "") !== String(savedVisionTitle || "");

    const saveVisionTitle = throttle(async () => {
        if (!selectedItem?._id || titleSaving) {
            return;
        }
        setTitleSaving(true);
        try {
            const result = await authApi.post(
                "admin/customer-gallery/" + selectedItem._id + "/vision/title",
                { title: titleDraft.trim() }
            );
            const updated = result.data;
            if (updated) {
                mergeGalleryItem(updated);
                const nextTitle = updated.title || updated.vision?.title || "";
                setTitleDraft(nextTitle ? String(nextTitle) : "");
            }
            displayAlert("success", "Updated", "Vision title saved");
        } catch (error) {
            const message =
                error?.response?.data?.message || error?.message || "Failed to save vision title";
            displayAlert("error", "Update failed", message);
        } finally {
            setTitleSaving(false);
        }
    }, 500, { trailing: false });

    const clearVisionTitle = throttle(async () => {
        if (!selectedItem?._id || titleSaving) {
            return;
        }
        setTitleSaving(true);
        try {
            const result = await authApi.post(
                "admin/customer-gallery/" + selectedItem._id + "/vision/title",
                { title: "" }
            );
            const updated = result.data;
            if (updated) {
                mergeGalleryItem(updated);
                setTitleDraft("");
            }
            displayAlert("success", "Updated", "Vision title cleared");
        } catch (error) {
            const message =
                error?.response?.data?.message || error?.message || "Failed to clear vision title";
            displayAlert("error", "Update failed", message);
        } finally {
            setTitleSaving(false);
        }
    }, 500, { trailing: false });

    const openProviderDialog = async () => {
        const providerId = selectedItem?.providerId;
        if (!providerId) {
            return;
        }
        setProviderDialogVisible(true);
        setProviderDialogLoading(true);
        setProviderDialogData(null);
        try {
            const result = await authApi.post(
                "admin/provider/details?providerId=" + encodeURIComponent(providerId),
                {}
            );
            setProviderDialogData(result.data || null);
        } catch (e) {
            displayAlert("error", "Error", "Failed to load provider details");
            setProviderDialogVisible(false);
        } finally {
            setProviderDialogLoading(false);
        }
    };

    const closeProviderDialog = () => {
        setProviderDialogVisible(false);
        setProviderDialogData(null);
        setProviderDialogLoading(false);
    };

    const resolveProviderDisplayName = () => {
        if (!providerDialogData) {
            return null;
        }
        return providerDialogData.profile?.name || providerDialogData.provider?.name || null;
    };

    const resolveGalleryBaseUrl = () => {
        const vanityName = providerDialogData?.profile?.vanityName;
        if (!vanityName || !process.env.REACT_APP_NETWORK_SITE) {
            return null;
        }
        return process.env.REACT_APP_NETWORK_SITE + "/company/" + vanityName + "/gallery";
    };

    const filteredProviderId =
        lazyState.providerId && isValidObjectId(lazyState.providerId) ? lazyState.providerId.trim() : null;
    const pageHasMissingVariants = items.some(
        (item) => item && !hasSizeVariants(item) && !isUnsupportedForVariants(item)
    );
    const showBulkProvision = !!filteredProviderId && pageHasMissingVariants;

    const provisionVariantsForProvider = throttle(async () => {
        if (!filteredProviderId || bulkProvisionLoading) {
            return;
        }
        setBulkProvisionLoading(true);
        try {
            const result = await authApi.post("admin/customer-gallery/provision-variants", {
                providerId: filteredProviderId,
            });
            const queued = result.data?.queued || 0;
            const skipped = result.data?.skipped || 0;
            const errors = result.data?.errors || 0;
            if (queued > 0) {
                displayAlert(
                    "success",
                    "Queued",
                    "Create variants & analyze queued for " +
                        queued +
                        " image" +
                        (queued === 1 ? "" : "s") +
                        (skipped > 0 ? " (" + skipped + " skipped)" : "")
                );
            } else if (errors > 0) {
                displayAlert("error", "Provision failed", "Could not queue variant tasks (" + errors + " errors)");
            } else {
                displayAlert("info", "Nothing to queue", "No images needed variant generation");
            }
            refetch();
        } catch (error) {
            const message =
                error?.response?.data?.message || error?.message || "Failed to queue variant generation";
            displayAlert("error", "Provision failed", message);
        } finally {
            setBulkProvisionLoading(false);
        }
    }, 500, { trailing: false });

    const pageHeader = (
        <PageHeading pageTitle="Admin - Customer Gallery">
            {showBulkProvision ? (
                <Button
                    label="Create Variants & Analyze"
                    icon="pi pi-images"
                    className="p-button-sm"
                    onClick={provisionVariantsForProvider}
                    loading={bulkProvisionLoading}
                    disabled={bulkProvisionLoading}
                />
            ) : (
                <div className="block" />
            )}
        </PageHeading>
    );

    const filterBar = (
        <div className="flex flex-row flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <div className="flex flex-row flex-wrap align-items-center gap-2 flex-grow-1">
                <div className="font-bold white-space-nowrap">Filter by</div>
                <InputText
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search..."
                    aria-label="Search Customer Gallery"
                    style={{ width: "18rem", maxWidth: "100%" }}
                />
                {searchInput ? (
                    <Button
                        type="button"
                        icon="pi pi-times"
                        className="p-button-outlined p-button-rounded p-button-sm"
                        onClick={() => setSearchInput("")}
                        aria-label="Clear search"
                        tooltip="Clear search"
                        tooltipOptions={{ position: "top" }}
                    />
                ) : null}
                <InputText
                    value={providerIdInput}
                    onChange={(event) => setProviderIdInput(event.target.value)}
                    placeholder="Provider ID"
                    aria-label="Filter by provider ID"
                    className={providerIdFilterInvalid ? "p-invalid" : undefined}
                    style={{ width: "16rem", maxWidth: "100%" }}
                />
                {providerIdInput ? (
                    <Button
                        type="button"
                        icon="pi pi-times"
                        className="p-button-outlined p-button-rounded p-button-sm"
                        onClick={() => setProviderIdInput("")}
                        aria-label="Clear provider ID"
                        tooltip="Clear provider ID"
                        tooltipOptions={{ position: "top" }}
                    />
                ) : null}
                {providerIdFilterInvalid ? (
                    <span className="text-sm text-red-500 white-space-nowrap">
                        Provider ID must be a 24-character ObjectId
                    </span>
                ) : null}
                <Dropdown
                    value={lazyState.hasVariants}
                    options={HAS_VARIANTS_OPTIONS}
                    onChange={(event) =>
                        setLazyState((prev) => ({
                            ...prev,
                            first: 0,
                            page: 0,
                            hasVariants: event.value || "",
                        }))
                    }
                    placeholder="Has variants"
                    showClear
                    aria-label="Filter by has variants"
                />
                <Button
                    icon="pi pi-refresh"
                    className="p-button-outlined"
                    onClick={() => refetch()}
                    disabled={isLoading}
                    aria-label="Refresh"
                    tooltip="Refresh"
                    tooltipOptions={{ position: "top" }}
                />
            </div>
            <div className="flex align-items-center gap-2 ml-auto">
                <div className="font-bold white-space-nowrap">Sort by</div>
                <Dropdown
                    value={lazyState.sortValue}
                    options={SORT_OPTIONS}
                    onChange={(event) =>
                        setLazyState((prev) => ({
                            ...prev,
                            first: 0,
                            page: 0,
                            sortValue: event.value,
                        }))
                    }
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Sort by"
                    aria-label="Sort Customer Gallery"
                />
            </div>
        </div>
    );

    if (isError) {
        return (
            <div>
                {pageHeader}
                <div className="layout-content">
                    {filterBar}
                    <PageLoadError />
                </div>
            </div>
        );
    }

    const selectedVariants = selectedItem ? getSortedVariants(selectedItem) : [];
    const originalVariant = selectedVariants.find((variant) => hasUsedFor(variant, "original")) || null;
    const canCreateVariants =
        selectedItem &&
        !hasSizeVariants(selectedItem) &&
        !isUnsupportedForVariants(selectedItem) &&
        !createVariantsLoading &&
        pollingId !== selectedItem._id;
    const visionEligible = selectedItem?.visionEligible !== false;
    const canReanalyzeVision =
        selectedItem &&
        hasSizeVariants(selectedItem) &&
        visionEligible &&
        !isUnsupportedForVariants(selectedItem) &&
        !reanalyzeLoading &&
        reanalyzePollingId !== selectedItem._id;
    const isReanalyzePolling = selectedItem && reanalyzePollingId === selectedItem._id;
    const isVariantPolling = selectedItem && pollingId === selectedItem._id;

    const viewportWidth =
        VIEWPORT_OPTIONS.find((option) => option.value === previewViewport)?.width || 1920;

    const lightboxHeader = (
        <div className="flex align-items-center justify-content-between gap-3 w-full pr-2" style={{ minWidth: 0 }}>
            <span className="font-semibold white-space-nowrap overflow-hidden text-overflow-ellipsis">
                {selectedItem?.friendlyImageName || selectedItem?._id || "Customer Gallery"}
            </span>
            <div className="flex align-items-center gap-2 flex-shrink-0">
                {hasSizeVariants(selectedItem) ? (
                    <>
                        {canReanalyzeVision || isReanalyzePolling ? (
                            <Button
                                label="Reanalyze vision"
                                icon="pi pi-eye"
                                className="p-button-sm p-button-outlined"
                                onClick={reanalyzeVision}
                                loading={reanalyzeLoading || isReanalyzePolling}
                                disabled={!canReanalyzeVision && !isReanalyzePolling}
                            />
                        ) : null}
                        {isReanalyzePolling ? (
                            <Tag value="Reanalyzing vision…" severity="info" />
                        ) : null}
                    </>
                ) : (
                    <>
                        {canCreateVariants || isVariantPolling ? (
                            <Button
                                label="Create size variants"
                                icon="pi pi-images"
                                className="p-button-sm"
                                onClick={createVariants}
                                loading={createVariantsLoading || isVariantPolling}
                                disabled={!canCreateVariants && !isVariantPolling}
                            />
                        ) : null}
                        {isVariantPolling ? (
                            <Tag value="Generating variants…" severity="info" />
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );

    const lightboxSidePanel = selectedItem ? (
        <aside
            className="flex flex-column surface-ground border-left-1 surface-border"
            style={{ width: "500px", minWidth: "500px", maxWidth: "500px", height: "100%" }}
        >
            <div className="p-3 border-bottom-1 surface-border">
                <div className="text-xs font-semibold text-500 uppercase mb-2">Preview size</div>
                {!isVideoItem(selectedItem) ? (
                    <SelectButton
                        value={previewViewport}
                        options={VIEWPORT_OPTIONS}
                        onChange={(event) => {
                            if (event.value) {
                                setPreviewViewport(event.value);
                            }
                        }}
                        itemTemplate={viewportTemplate}
                        optionLabel="label"
                        optionValue="value"
                        className="w-full"
                    />
                ) : (
                    <div className="text-sm text-600">Not available for video</div>
                )}
            </div>

            <div className="p-3 flex-1 overflow-auto">
                <div className="text-xs font-semibold text-500 uppercase mb-3">Details</div>
                <DetailRow label="ID">{selectedItem._id}</DetailRow>
                <DetailRow label="Provider ID">
                    {selectedItem.providerId ? (
                        <button
                            type="button"
                            className="p-0 m-0 border-none bg-transparent cursor-pointer text-primary text-sm p-link word-break-all text-left"
                            onClick={openProviderDialog}
                        >
                            {selectedItem.providerId}
                        </button>
                    ) : null}
                </DetailRow>
                <DetailRow label="Friendly name">{selectedItem.friendlyImageName}</DetailRow>
                <DetailRow label="Image name">{selectedItem.imageName}</DetailRow>
                <DetailRow label="Uploaded">{formatDate(selectedItem.uploadedAt)}</DetailRow>
                <DetailRow label="Photo source">{selectedItem.photoSource}</DetailRow>
                <div className="mb-3">
                    <div className="text-xs text-500 mb-1">Vision title</div>
                    <InputText
                        value={titleDraft}
                        onChange={(event) => setTitleDraft(event.target.value)}
                        className="w-full"
                        placeholder="Photo title"
                        aria-label="Vision title"
                    />
                    <div className="flex gap-2 mt-2">
                        <Button
                            label="Save"
                            icon="pi pi-check"
                            className="p-button-sm"
                            disabled={!titleDirty || titleSaving}
                            loading={titleSaving}
                            onClick={saveVisionTitle}
                        />
                        <Button
                            label="Clear"
                            icon="pi pi-times"
                            className="p-button-sm p-button-text"
                            disabled={(!savedVisionTitle && !titleDraft.trim()) || titleSaving}
                            onClick={clearVisionTitle}
                        />
                    </div>
                </div>
                {(selectedItem.description || selectedItem.vision?.description) && (
                    <DetailRow label="Vision description">
                        {selectedItem.description || selectedItem.vision?.description}
                    </DetailRow>
                )}
                <RemovableCatalogTags
                    label="Categories"
                    kind="categories"
                    items={selectedItem.vision?.categories}
                    removingKey={removingVisionTagKey}
                    onRemove={removeVisionTag}
                    allowEmpty={!!selectedItem.vision}
                />
                <RemovableCatalogTags
                    label="Subcategories"
                    kind="subcategories"
                    items={selectedItem.vision?.subcategories}
                    removingKey={removingVisionTagKey}
                    onRemove={removeVisionTag}
                    allowEmpty={!!selectedItem.vision}
                />
                <RemovableCatalogTags
                    label="Offers"
                    kind="offers"
                    items={selectedItem.vision?.offers}
                    removingKey={removingVisionTagKey}
                    onRemove={removeVisionTag}
                    allowEmpty={!!selectedItem.vision}
                />
                {(() => {
                    const analyzers = selectedItem.vision?.analyzers;
                    if (!analyzers || !analyzers.length) {
                        return null;
                    }
                    let analysisError = null;
                    for (let i = 0; i < analyzers.length; i++) {
                        if (analyzers[i]?.analysisError) {
                            analysisError = analyzers[i].analysisError;
                            break;
                        }
                    }
                    if (!analysisError) {
                        return null;
                    }
                    return (
                        <DetailRow label="Vision error">
                            <span className="text-red-500">{analysisError}</span>
                        </DetailRow>
                    );
                })()}
                {hasSizeVariants(selectedItem) ? (
                    !isUnsupportedForVariants(selectedItem) ? (
                        <div className="mt-2 mb-2">
                            <Button
                                label="Reanalyze vision"
                                icon="pi pi-eye"
                                className="w-full p-button-outlined"
                                onClick={reanalyzeVision}
                                loading={reanalyzeLoading || isReanalyzePolling}
                                disabled={!canReanalyzeVision && !isReanalyzePolling}
                            />
                            {selectedItem.visionEligible === false ? (
                                <div className="text-sm text-600 mt-2">
                                    Vision requires a paid or trial subscription and at least one site service or
                                    product offer.
                                </div>
                            ) : null}
                        </div>
                    ) : null
                ) : !isUnsupportedForVariants(selectedItem) ? (
                    <div className="mt-3">
                        <Button
                            label="Create size variants"
                            icon="pi pi-images"
                            className="w-full"
                            onClick={createVariants}
                            loading={createVariantsLoading || isVariantPolling}
                            disabled={!canCreateVariants && !isVariantPolling}
                        />
                    </div>
                ) : null}
                <DetailRow label="Has variants">
                    {hasSizeVariants(selectedItem) ? (
                        <Chip label="Yes" className="text-sm" />
                    ) : (
                        <Chip label="No" className="text-sm" />
                    )}
                </DetailRow>
                {selectedItem.width != null && selectedItem.height != null && (
                    <DetailRow label="Dimensions">
                        {selectedItem.width}×{selectedItem.height}px
                    </DetailRow>
                )}
                <DetailRow label="Aspect ratio">{selectedItem.aspectRatioLabel}</DetailRow>
                <DetailRow label="Orientation">{selectedItem.orientation}</DetailRow>
                {selectedItem.hasAlpha != null && (
                    <DetailRow label="Has alpha">{selectedItem.hasAlpha ? "Yes" : "No"}</DetailRow>
                )}
                <DetailRow label="Original format">{originalVariant?.format}</DetailRow>
                <DetailRow label="File size">
                    {formatFileSize(
                        selectedItem.size != null && selectedItem.size > 0
                            ? selectedItem.size
                            : originalVariant?.size
                    )}
                </DetailRow>
                <DetailRow label="Public path">{selectedItem.publicPath}</DetailRow>

                {selectedItem.variantProcessingError && (
                    <div className="mt-2 pt-3 border-top-1 surface-border">
                        <DetailRow label="Variant error">
                            <span className="text-red-500">{selectedItem.variantProcessingError}</span>
                        </DetailRow>
                    </div>
                )}

                {selectedVariants.length > 0 && (
                    <div className="mt-2 pt-3 border-top-1 surface-border">
                        <div className="text-xs font-semibold text-500 uppercase mb-3">Variants</div>
                        <Accordion multiple>
                            {selectedVariants.map((variant, index) => {
                                const sizeLabel = formatFileSize(variant.size);
                                const header = sizeLabel
                                    ? `${variantLabel(variant)} · ${sizeLabel}`
                                    : variantLabel(variant);
                                return (
                                    <AccordionTab
                                        key={(variant.s3FileName || variant.publicUrl || "variant") + "-" + index}
                                        header={header}
                                    >
                                        <DetailRow label="Used for">{formatUsedFor(variant)}</DetailRow>
                                        {variant.width != null && variant.height != null && (
                                            <DetailRow label="Dimensions">
                                                {variant.width}×{variant.height}px
                                            </DetailRow>
                                        )}
                                        <DetailRow label="Format">{variant.format}</DetailRow>
                                        <DetailRow label="Content type">{variant.contentType}</DetailRow>
                                        <DetailRow label="File size">{sizeLabel}</DetailRow>
                                        <DetailRow label="S3 key">{variant.s3FileName}</DetailRow>
                                        <DetailRow label="Public URL">{variant.publicUrl}</DetailRow>
                                    </AccordionTab>
                                );
                            })}
                        </Accordion>
                    </div>
                )}
            </div>
        </aside>
    ) : null;

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card">
                    {filterBar}
                    {isLoading && (
                        <div className="flex justify-content-center p-4">
                            <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                        </div>
                    )}

                    {!isLoading && items.length === 0 && (
                        <div className="text-center text-700 p-5">
                            No customer gallery images found for these filters.
                        </div>
                    )}

                    {!isLoading && items.length > 0 && (
                        <>
                            <div className="grid">
                                {items.map((item) => (
                                    <div key={item._id} className="col-6 sm:col-4 md:col-3">
                                        <button
                                            type="button"
                                            className="p-0 border-none border-round overflow-hidden cursor-pointer w-full surface-100 relative"
                                            style={{
                                                aspectRatio: "1 / 1",
                                                display: "block",
                                                background: "transparent",
                                            }}
                                            onClick={() => openLightbox(item)}
                                        >
                                            {isVideoItem(item) ? (
                                                <>
                                                    <video
                                                        src={item.publicPath}
                                                        muted
                                                        playsInline
                                                        preload="metadata"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "contain",
                                                            display: "block",
                                                        }}
                                                    />
                                                    <span
                                                        className="absolute flex align-items-center justify-content-center border-circle bg-black-alpha-60 text-white"
                                                        style={{
                                                            top: "50%",
                                                            left: "50%",
                                                            width: "2.5rem",
                                                            height: "2.5rem",
                                                            transform: "translate(-50%, -50%)",
                                                            pointerEvents: "none",
                                                        }}
                                                        aria-hidden="true"
                                                    >
                                                        <i className="pi pi-play" style={{ fontSize: "1rem" }} />
                                                    </span>
                                                </>
                                            ) : (
                                                <img
                                                    src={getImageSrc(item)}
                                                    srcSet={buildSrcSet(item)}
                                                    sizes="(max-width: 576px) 50vw, (max-width: 768px) 33vw, 25vw"
                                                    alt={item.friendlyImageName || "Gallery image"}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "contain",
                                                        display: "block",
                                                    }}
                                                />
                                            )}
                                            {!hasSizeVariants(item) ? (
                                                <span
                                                    className="absolute flex align-items-center justify-content-center border-round bg-black-alpha-60 text-white"
                                                    style={{
                                                        top: "0.4rem",
                                                        left: "0.4rem",
                                                        height: "1.5rem",
                                                        padding: "0 0.4rem",
                                                        fontSize: "0.7rem",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    original
                                                </span>
                                            ) : null}
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <Paginator
                                first={lazyState.first}
                                rows={lazyState.rows}
                                totalRecords={totalRecords}
                                rowsPerPageOptions={[25, 50, 100]}
                                onPageChange={onPageChange}
                                className="mt-3"
                            />
                        </>
                    )}
                </div>
            </div>

            <Dialog
                header={lightboxHeader}
                visible={showLightbox && !!selectedItem}
                onHide={closeLightbox}
                style={{ width: "95vw", height: "92vh" }}
                contentStyle={{ height: "calc(92vh - 5rem)", padding: 0, overflow: "hidden" }}
                maximizable
                modal
                blockScroll
            >
                {selectedItem && (
                    <div className="flex w-full h-full overflow-hidden">
                        <div className="flex-1 flex align-items-center justify-content-center surface-900 overflow-auto p-3">
                            {isVideoItem(selectedItem) ? (
                                <video
                                    src={selectedItem.publicPath}
                                    controls
                                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                                />
                            ) : (
                                <img
                                    src={getImageSrc(selectedItem)}
                                    srcSet={buildSrcSet(selectedItem)}
                                    sizes={`${viewportWidth}px`}
                                    alt={selectedItem.friendlyImageName || "Gallery image"}
                                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                />
                            )}
                        </div>
                        {lightboxSidePanel}
                    </div>
                )}
            </Dialog>

            <Dialog
                header="Provider"
                visible={providerDialogVisible}
                onHide={closeProviderDialog}
                style={{ width: "min(480px, 95vw)" }}
                modal
                dismissableMask
            >
                {providerDialogLoading ? (
                    <div className="flex justify-content-center py-4">
                        <ProgressSpinner style={{ width: "40px", height: "40px" }} />
                    </div>
                ) : (
                    <div className="flex flex-column gap-3">
                        <div>
                            <div className="text-xs text-500 mb-1">Provider name</div>
                            <div className="font-medium">{resolveProviderDisplayName() || "-"}</div>
                        </div>
                        <div>
                            <div className="text-xs text-500 mb-1">Provider ID</div>
                            <div className="font-medium word-break-all">{selectedItem?.providerId || "-"}</div>
                        </div>
                        {resolveGalleryBaseUrl() ? (
                            <>
                                <div>
                                    <div className="text-xs text-500 mb-1">Gallery page</div>
                                    <a
                                        href={resolveGalleryBaseUrl()}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary"
                                    >
                                        Open gallery
                                        <i className="pi pi-external-link ml-2" style={{ fontSize: "0.8rem" }} />
                                    </a>
                                </div>
                                {selectedItem?.friendlyImageName ? (
                                    <div>
                                        <div className="text-xs text-500 mb-1">This photo</div>
                                        <a
                                            href={resolveGalleryBaseUrl() + "/" + selectedItem.friendlyImageName}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary"
                                        >
                                            Open photo in gallery
                                            <i className="pi pi-external-link ml-2" style={{ fontSize: "0.8rem" }} />
                                        </a>
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="text-600 text-sm">
                                Gallery links are unavailable because this provider has no vanity URL.
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default CustomerGallery;
